import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Table } from '@fikasio/react-ui-components';
import EmptyTableRow from 'components/UI/EmptyTableRow';
import { DueDatePreset, matchesDueDatePreset } from 'components/filters/dueDatePreset';
import { nestedChildIds, withoutCollapsedDescendants } from 'utils/nestedRows';
import { RangeSelectEvent } from 'utils/rangeSelection';
import { useGetTasksQuery } from 'services/tasks/api';
import { OpenStatusDropdownProvider } from 'components/tasks/TasksViewer/TaskStatusDropdown';
import ProjectRow from './ProjectRow';
import { calculateIncompleteTime, calculateCompletionPercentage } from '../tasks/utils';
import {
  childIdsByParent,
  nestProjects,
  projectDescendantDepth,
  projectMatchesAssignees,
  projectStatus,
  sortProjectsByName,
} from './utils';
import styles from './ProjectsList.module.css';

const ProjectsList = ({
  assignees = [] as string[],
  dueDatePreset = 'ALL' as DueDatePreset,
  onAddProject,
  onProjectClick,
  onProjectSelect,
  onSelectAllProjects,
  projects,
  selectedProjects,
  showCompleteProjects,
  showIncompleteProjects,
  showCancelledProjects,
  showArchivedProjects,
}) => {
  const { t } = useTranslation();
  const { data } = useGetTasksQuery({});
  const tasks = data?.tasks;
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() => new Set());
  const [pinnedNames, setPinnedNames] = useState<Record<string, string>>({});

  const toggleCollapsed = (projectId: string) => {
    setCollapsedIds(current => {
      const next = new Set(current);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const reveal = (projectId?: string | null) => {
    if (!projectId) {
      return;
    }
    setCollapsedIds(current => {
      if (!current.has(projectId)) {
        return current;
      }
      const next = new Set(current);
      next.delete(projectId);
      return next;
    });
  };

  const handleEditingNameChange = (projectId: string | undefined, editing: boolean) => {
    if (!projectId) {
      return;
    }
    if (editing) {
      const pinnedName = projects?.find(item => item.id === projectId)?.name ?? '';
      setPinnedNames(current => (
        projectId in current ? current : { ...current, [projectId]: pinnedName }
      ));
      return;
    }
    setPinnedNames(current => {
      if (!(projectId in current)) {
        return current;
      }
      const next = { ...current };
      delete next[projectId];
      return next;
    });
  };

  const projectsToShow = projects && sortProjectsByName(
    projects.map(project => {
      const projectTasks = tasks?.filter(task => task.projects?.some(p => p.id === project.id));
      const completionRatio = calculateCompletionPercentage(projectTasks);
      const incompleteTime = calculateIncompleteTime(projectTasks);
      return {
        ...project,
        completionRatio,
        incompleteTime,
      };
    })
    .filter(project => {
      const status = projectStatus(project);
      return (showCompleteProjects && status === 'Completed' && !project.isArchived)
        || (showIncompleteProjects && status !== 'Completed' && status !== 'Cancelled' && !project.isArchived)
        || (showCancelledProjects && status === 'Cancelled' && !project.isArchived)
        || (showArchivedProjects && project.isArchived);
    })
    .filter(project => matchesDueDatePreset(project.dueAt, dueDatePreset as DueDatePreset))
    .filter(project => projectMatchesAssignees(project, assignees)),
    pinnedNames,
  );

  const nestedProjects = nestProjects(projectsToShow || []);
  const collapsibleIds = nestedChildIds(nestedProjects, row => row.project.id);
  const visibleProjects = withoutCollapsedDescendants(
    nestedProjects,
    row => !!row.project.id && collapsedIds.has(row.project.id),
  );
  const displayedProjects = visibleProjects.map(row => row.project);
  const childrenByParent = childIdsByParent(projects || []);

  const allProjectsAreChecked = !!projectsToShow?.length
    && (projectsToShow?.length === selectedProjects.length);

  const addProject = async project => {
    reveal(project.parentProject);
    onAddProject(project)
      .then(async ({ data: resultProject }) => {
        if (resultProject) {
          await new Promise(resolve => setTimeout(resolve, 200));
          const elementsWithClassname = document.getElementsByClassName(resultProject.id!);
          const nbElementsWithClassname = elementsWithClassname.length;
          if (nbElementsWithClassname) {
            (elementsWithClassname[nbElementsWithClassname - 1] as HTMLElement).focus();
          }
        }
      });
  };

  return projects && (
    <div className={styles.projectsList}>
      <OpenStatusDropdownProvider>
      <Table
        bordered
        hover
        responsive
      >
        <thead>
          <tr>
            <th
              style={{ width: 35 }}
            >
              <Checkbox
                isChecked={allProjectsAreChecked}
                onClick={() => {
                  if (allProjectsAreChecked) {
                    onSelectAllProjects([]);
                  } else {
                    onSelectAllProjects(projectsToShow);
                  }
                }}
              />
            </th>
            <th style={{ width: 35 }} />
            <th />
            <th style={{ width: 150 }} />
            <th style={{ width: 150 }} />
            <th style={{ width: 150 }} />
            <th style={{ width: 35 }} />
            <th style={{ width: 35 }} />
          </tr>
        </thead>
        <tbody>
          {
            (!projectsToShow || !projectsToShow.length) && (
              <EmptyTableRow
                colSpan={8}
                message={t('noProjectsToDisplay')}
              />
            )
          }
          {
            visibleProjects.map(({ depth, parent, previousSibling, project }) => (
              <ProjectRow
                canCollapse={!!project.id && collapsibleIds.has(project.id)}
                depth={depth}
                descendantDepth={projectDescendantDepth(project.id, childrenByParent)}
                isCollapsed={!!project.id && collapsedIds.has(project.id)}
                isSelected={!!selectedProjects.find(p1 => project.id === p1.id)}
                key={project.id}
                onAddProject={addProject}
                onClick={onProjectClick}
                onEditingNameChange={editing => handleEditingNameChange(project.id, editing)}
                onSelect={(selectedProject, event?: RangeSelectEvent) => onProjectSelect(
                  selectedProject,
                  event,
                  displayedProjects,
                )}
                onToggleCollapsed={() => project.id && toggleCollapsed(project.id)}
                parent={parent}
                previousSibling={previousSibling}
                project={project}
              />
            ))
          }
        </tbody>
        </Table>
      </OpenStatusDropdownProvider>
    </div>
  );
};

export default ProjectsList;
